import {useEffect, useRef, useState} from 'react'

import {LatestOrderChangeRequest} from '../types/orderChangeBanner.types'

/** Delay before refreshing to allow backend item-level data to propagate (10 seconds) */
const PROPAGATION_DELAY_MS = 10000

/**
 * Detects when changesApplied transitions to true for manual approval requests
 * and triggers an auto-refresh of order data to get fresh orderStatus from Redux.
 * Delays the refresh by 10s to allow item-level data to propagate in the backend.
 */
export const useAutoRefreshOnManualApproval = (
  latestRequest: LatestOrderChangeRequest | null | undefined,
  onOrderDataChanged?: () => void,
  orderId?: string
) => {
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false)
  const prevChangesAppliedRef = useRef<boolean | undefined>(undefined)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset all state when switching to a different order
  useEffect(() => {
    setIsAutoRefreshing(false)
    prevChangesAppliedRef.current = undefined
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [orderId])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    const requiresManualApproval = latestRequest?.requestData?.requiresManualApproval ?? false
    const changesApplied = latestRequest?.changesApplied
    const prevChangesApplied = prevChangesAppliedRef.current

    if (requiresManualApproval && changesApplied === true && prevChangesApplied === false) {
      setIsAutoRefreshing(true)
      // Delay the refresh to allow backend item-level data to propagate
      timerRef.current = setTimeout(() => {
        onOrderDataChanged?.()
        setIsAutoRefreshing(false)
        timerRef.current = null
      }, PROPAGATION_DELAY_MS)
    }

    prevChangesAppliedRef.current = changesApplied
  }, [
    latestRequest?.changesApplied,
    latestRequest?.requestData?.requiresManualApproval,
    onOrderDataChanged
  ])

  return isAutoRefreshing
}
